import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ETATS_RECLAMATION_RESEAU } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Reclamation {
  id: string;
  signalement_reference: string | null;
  source: string;
  escalade_direction: boolean;
  escalade_par: string | null;
  contenu: string;
  reponse: string;
  statut: keyof typeof ETATS_RECLAMATION_RESEAU;
  version: number;
  redige_par: string | null;
}
export function PageReclamationsReseau() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const q = useQuery({
    queryKey: ['reclamations-reseau', contexte?.appartenance_active?.id],
    queryFn: () => requete<{ elements: Reclamation[] }>('/reclamations-reseau'),
    enabled: peut(contexte, 'reclamation', 'voir'),
  });
  if (!peut(contexte, 'reclamation', 'voir'))
    return <p role="alert">Accès aux réclamations non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Réclamations des usagers" />
      <p>
        Réclamations liées à un signalement et réclamations reçues directement par l’exploitant.
        Toute réponse exige la validation d’un autre compte habilité. Pour une réception en agence,
        par téléphone ou courrier, la réponse validée doit être communiquée à l’usager par votre
        service ; aucun envoi automatique.
      </p>
      {peut(contexte, 'reclamation', 'modifier') && (
        <details>
          <summary>Enregistrer une réclamation générale</summary>
          <form
            style={{ display: 'grid', gap: 12, maxWidth: 720 }}
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const d = new FormData(form);
              setOccupe(true);
              setErreur('');
              try {
                await requete('/reclamations-reseau', {
                  methode: 'POST',
                  corps: { source: d.get('source'), contenu: d.get('contenu') },
                });
                form.reset();
                await client.invalidateQueries({ queryKey: ['reclamations-reseau'] });
              } catch (err) {
                setErreur(messageErreur(err));
              } finally {
                setOccupe(false);
              }
            }}
          >
            <p>
              Réception interne sans signalement préalable. Décrire la demande sans mot de passe,
              pièce d’identité ni donnée personnelle inutile.
            </p>
            <label>
              Canal de réception{' '}
              <select name="source">
                <option value="ACCUEIL">Accueil en agence</option>
                <option value="TELEPHONE">Téléphone</option>
                <option value="COURRIER">Courrier</option>
              </select>
            </label>
            <label>
              Objet de la réclamation générale{' '}
              <textarea name="contenu" required minLength={10} maxLength={2000} rows={4} />
            </label>
            <button disabled={occupe}>Enregistrer la réclamation générale</button>
          </form>
        </details>
      )}
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {q.isPending && <p role="status">Chargement des réclamations…</p>}
      {q.data && <p>{q.data.elements.length} réclamation(s), au maximum les 500 plus récentes.</p>}
      {q.data?.elements.map((r) => {
        const modifier =
          peut(contexte, 'reclamation', 'modifier') && ['RECEIVED', 'IN_REVIEW'].includes(r.statut);
        const valider =
          peut(contexte, 'reclamation', 'valider') &&
          r.statut === 'PENDING_VALIDATION' &&
          r.redige_par !== contexte?.compte.id;
        return (
          <article key={r.id}>
            <h2>{r.signalement_reference ?? `Réclamation générale ${r.id}`}</h2>
            {!r.signalement_reference && (
              <p>
                Réception :{' '}
                {
                  (
                    {
                      ACCUEIL: 'Accueil en agence',
                      TELEPHONE: 'Téléphone',
                      COURRIER: 'Courrier',
                      PUBLIC: 'Portail citoyen',
                    } as Record<string, string>
                  )[r.source]
                }
              </p>
            )}
            <p>{ETATS_RECLAMATION_RESEAU[r.statut]}</p>
            <p>{r.contenu}</p>
            {r.escalade_direction && (
              <p role="status">
                <strong>Attention de la direction demandée</strong>
              </p>
            )}
            {((!r.escalade_direction && peut(contexte, 'reclamation', 'modifier')) ||
              (r.escalade_direction &&
                peut(contexte, 'reclamation', 'valider') &&
                r.escalade_par !== contexte?.compte.id)) && (
              <details>
                <summary>
                  {r.escalade_direction
                    ? 'Traiter l’escalade interne'
                    : 'Demander l’attention de la direction'}
                </summary>
                <p>
                  Suivi interne à votre organisation. Cette action ne saisit pas le régulateur, ne
                  modifie pas la réponse et ne transmet aucun message externe. Le traitement exige
                  un autre compte habilité avec second facteur.
                </p>
                <form
                  key={`escalade-${r.version}`}
                  style={{ display: 'grid', gap: 12, maxWidth: 720 }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const d = new FormData(e.currentTarget);
                    setOccupe(true);
                    setErreur('');
                    try {
                      await requete(`/reclamations-reseau/${r.id}/escalade`, {
                        methode: 'POST',
                        corps: {
                          action: r.escalade_direction ? 'traiter' : 'signaler',
                          version: r.version,
                          motif: d.get('motif'),
                        },
                      });
                      await client.invalidateQueries({ queryKey: ['reclamations-reseau'] });
                    } catch (err) {
                      setErreur(messageErreur(err));
                    } finally {
                      setOccupe(false);
                    }
                  }}
                >
                  <label>
                    Motif de l’escalade ou compte rendu{' '}
                    <textarea name="motif" required minLength={10} maxLength={500} />
                  </label>
                  <button disabled={occupe}>
                    {r.escalade_direction
                      ? 'Confirmer le traitement interne'
                      : 'Confirmer la demande à la direction'}
                  </button>
                </form>
              </details>
            )}
            {r.reponse && (
              <>
                <h3>
                  {r.statut === 'ANSWERED'
                    ? r.signalement_reference || r.source === 'PUBLIC'
                      ? 'Réponse publiée'
                      : 'Réponse validée à communiquer'
                    : 'Projet de réponse (interne)'}
                </h3>
                <p>{r.reponse}</p>
              </>
            )}
            {(modifier || valider) && (
              <form
                key={r.version}
                style={{ display: 'grid', gap: 12, maxWidth: 720 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const d = new FormData(e.currentTarget);
                  setOccupe(true);
                  setErreur('');
                  try {
                    await requete('/reclamations-reseau/' + r.id + '/actions', {
                      methode: 'POST',
                      corps: {
                        action: d.get('action'),
                        version: r.version,
                        motif: d.get('motif'),
                        reponse: d.get('reponse') ?? '',
                      },
                    });
                    await client.invalidateQueries({ queryKey: ['reclamations-reseau'] });
                  } catch (err) {
                    setErreur(messageErreur(err));
                  } finally {
                    setOccupe(false);
                  }
                }}
              >
                <label>
                  Action de réclamation
                  <select name="action">
                    {modifier ? (
                      <>
                        <option value="proposer_reponse">Soumettre la réponse à validation</option>
                        {r.statut === 'RECEIVED' && (
                          <option value="prendre_en_charge">Prendre en charge</option>
                        )}
                      </>
                    ) : (
                      <>
                        <option value="valider_reponse">
                          {r.signalement_reference || r.source === 'PUBLIC'
                            ? 'Valider et publier la réponse'
                            : 'Valider la réponse à communiquer'}
                        </option>
                        <option value="rejeter_reponse">Demander une correction</option>
                      </>
                    )}
                  </select>
                </label>
                {modifier && (
                  <label>
                    Réponse proposée
                    <textarea name="reponse" maxLength={4000} defaultValue={r.reponse} />
                  </label>
                )}
                <label>
                  Motif interne
                  <textarea name="motif" minLength={3} maxLength={500} required />
                </label>
                <button disabled={occupe}>Enregistrer le traitement</button>
              </form>
            )}
            <Historique id={r.id} version={r.version} />
          </article>
        );
      })}
    </section>
  );
}
function Historique({ id, version }: { id: string; version: number }) {
  const [ouvert, setOuvert] = useState(false);
  const q = useQuery({
    queryKey: ['reclamations-reseau', 'historique', id, version],
    queryFn: () =>
      requete<{
        elements: {
          action: string;
          statut: keyof typeof ETATS_RECLAMATION_RESEAU;
          motif: string;
          reponse: string;
          cree_le: string;
        }[];
      }>('/reclamations-reseau/' + id + '/historique'),
    enabled: ouvert,
  });
  return (
    <div>
      <button onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert}>
        Historique de la réclamation
      </button>
      {ouvert && q.error && <p role="alert">{messageErreur(q.error)}</p>}
      {ouvert && q.data && (
        <ol>
          {q.data.elements.map((h, i) => (
            <li key={i}>
              {new Date(h.cree_le).toLocaleString('fr-FR')} · {ETATS_RECLAMATION_RESEAU[h.statut]} ·{' '}
              {h.motif}
              {h.reponse && <p>{h.reponse}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
